import "../pages/composable/Composable.css";
import React, { useEffect, useState, useMemo, forwardRef, useRef } from "react";
import CmesTabs from "../pages/composable/CmesTabs";
import FactoryAndPanel from "../pages/composable/FactoryAndPanel";
import Process from "./Process";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ReactComponent as TulipLibrary } from "../svgs/tulipLibraryWhite.svg";
import { useApp } from "../hooks/useApp";
import { useAuth } from "../hooks/useAuth";
import MockPc from "../pages/composable/MockPc";
import Ipad from "../pages/composable/Ipad";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { PoweredByLogo } from "./Common/PoweredByLogo";
import { GroupButtons } from "./Common/GroupButtons";
import { CustomButton } from "./Common/CustomButton";
import Gobutton from "./Gobutton";
import SvgIcon from "@mui/material/SvgIcon";
import UnsSidebar from "../pages/uns/UnsSidebar";

const PROVEIT_MEGAFACTORY_ID = "zvvIoi5j80myiEVJLVj8";

const MegaFactoryContent = forwardRef(function MegaFactoryContent({
  activeMegaFactory,
  isModal = true,
  isFactoryDirect = false,
  hasSidebar = false,
  preconnectedMqttValues = null,
  preconnectedMqttRecentlyChanged = null,
  // Simulation props
  simulatedValues = null,
  simulatedRecentlyChanged = null,
  simulatedAgentStates = null,
  simulatedAgents = null,
  // Hide the discover section (title and description)
  hideDiscover = false,
}, ref) {
  const {
    setActiveMockPc,
    setActiveMegaFactory,
    vtecAssetsUnsorted: vtecAssets,
    devMode,
    setDevMode,
  } = useApp();
  const { user } = useAuth();
  const isTulipAdmin = !!user?.email?.endsWith('@tulip.co');
  const tabClickCount = useRef(0);
  const tabClickTimer = useRef(null);

  useEffect(() => {
    return () => clearTimeout(tabClickTimer.current);
  }, []);

  const handleTabClick = () => {
    if (!isTulipAdmin) return;
    tabClickCount.current += 1;
    clearTimeout(tabClickTimer.current);
    tabClickTimer.current = setTimeout(() => {
      tabClickCount.current = 0;
    }, 2000);
    if (tabClickCount.current >= 5) {
      tabClickCount.current = 0;
      setDevMode((prev) => !prev);
    }
  };
  const [exampleData, setExampleData] = useState(null);
  const [exampleSuites, setExampleSuites] = useState(null);
  const [selectedExampleSuite, setSelectedExampleSuite] = useState(null);
  const [tab, setTab] = useState("Composable");
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [cmesData, setCmesData] = useState(null);
  const theme = useTheme();

  // Helper function to find an asset by ID
  const findAssetById = (id, assets) => assets.find((asset) => asset.id === id);

  // Helper function to update children with real assets
  const updateChildrenWithRealAssets = (children, vtecAssets) => {
    // Handle undefined or null children (e.g., media factories don't have Children)
    if (!children || !Array.isArray(children)) return [];
    
    return children.map((child) => {
      const realAsset = findAssetById(child.id, vtecAssets);
      return realAsset ? { ...realAsset, ...child } : child;
    });
  };

  // Function to update suites with factory information
  const updateSuitesWithFactory = (asset, vtecAssets) => {
    if (!asset) return null;

    return {
      ...asset,
      suites: asset?.suites?.map((suite) => {
        const factory = findAssetById(suite.factoryId, vtecAssets);
        if (!factory) return suite; 

        // Resolve Children with real asset data (handles null/undefined gracefully)
        const updatedChildren = updateChildrenWithRealAssets(factory.Children, vtecAssets);

        return {
          ...suite,
          factory: {
            ...factory,
            Children: updatedChildren,
            Title: suite.title || factory.Title,
            color: suite.color || theme.palette.primary.main,
            discoverHeader: suite.textHeader,
            discover: suite.text,
          },
        };
      }),
    };
  };

  // Map version 1 mega factory assets
  const mapVersion1MegaFactory = (vtecAssets, activeMegaFactory) => {
    const cMES =
      findAssetById(activeMegaFactory["megafactory"], vtecAssets)?.Suites || [];
    const exampleFlow = findAssetById(
      activeMegaFactory["exampleflow"],
      vtecAssets
    );

    const cmesSuites = cMES.map((suite) => ({
      ...suite,
      color: theme.palette.info.main,
      Children: updateChildrenWithRealAssets(suite.Children, vtecAssets),
    }));

    setCmesData(cmesSuites);
    setSelectedSuite(cmesSuites[0]);

    if (exampleFlow) {
      setExampleData({
        ...exampleFlow,
        color: theme.palette.info.main,
        Children: updateChildrenWithRealAssets(
          exampleFlow.Children,
          vtecAssets
        ),
      });
    }
  };

  // Map version 2 mega factory assets
  const mapVersion2MegaFactory = (vtecAssets, activeMegaFactory) => {
    const newAsset = updateSuitesWithFactory(activeMegaFactory, vtecAssets);
    setExampleSuites(null);
    setSelectedExampleSuite(null);

    if (newAsset?.exampleflow) {
      const exampleFlow = findAssetById(newAsset.exampleflow, vtecAssets);
      if (exampleFlow) {
        if (exampleFlow.Type === "megafactory" && exampleFlow.suites) {
          const resolvedExample = updateSuitesWithFactory(exampleFlow, vtecAssets);
          const resolvedSuites = resolvedExample?.suites?.map((item) => item.factory).filter(Boolean);
          if (resolvedSuites?.length) {
            setExampleSuites(resolvedSuites);
            setSelectedExampleSuite(resolvedSuites[0]);
          }
          setExampleData({
            ...exampleFlow,
            color: newAsset.exampleColor || theme.palette.primary.main,
          });
        } else {
          setExampleData({
            ...exampleFlow,
            color: newAsset.exampleColor || theme.palette.primary.main,
            Children: updateChildrenWithRealAssets(
              exampleFlow.Children,
              vtecAssets
            ),
            discoverHeader: newAsset.exampleHeader,
            discover: newAsset.exampleText,
          });
        }
      }
    }

    const updatedSuites = newAsset?.suites?.map((item) => item.factory).filter(Boolean);
    setCmesData(updatedSuites);
    setSelectedSuite(updatedSuites?.length ? updatedSuites[0] : null);
  };

  // Main asset mapping function
  const mapAsset = (vtecAssets, activeMegaFactory) => {
    if (activeMegaFactory) {
      if (
        activeMegaFactory.hasOwnProperty("megafactory") &&
        activeMegaFactory["megafactory"]
      ) {
        mapVersion1MegaFactory(vtecAssets, activeMegaFactory);
      } else {
        mapVersion2MegaFactory(vtecAssets, activeMegaFactory);
      }
    }
  };

  // Effect hook to map assets on component mount
  useEffect(() => {
    mapAsset(vtecAssets, activeMegaFactory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vtecAssets, activeMegaFactory]);

  const handleClose = () => {
    setActiveMegaFactory(null);
  };
  const handleMockPc = (url) => {
    setActiveMockPc(url);
  };

  const handleTab = (tab) => {
    setTab(tab);
    setSelectedAsset(null);
  };

  const handleSuiteChange = (newSuiteTitle) => {
    const newSuite = cmesData.filter(
      (suite) => suite.Title === newSuiteTitle
    )[0];
    setSelectedSuite(newSuite);
    setSelectedAsset(null);
  };

  const handleExampleSuiteChange = (newSuiteTitle) => {
    const newSuite = exampleSuites.filter(
      (suite) => suite.Title === newSuiteTitle
    )[0];
    setSelectedExampleSuite(newSuite);
    setSelectedAsset(null);
  };

  const isExampleMegafactory = tab === "Example" && exampleSuites?.length > 0;

  const effectiveSelectedSuite = useMemo(() => {
    if (!cmesData?.length || !selectedSuite) return selectedSuite;
    const match = cmesData.find((s) => s?.Title === selectedSuite?.Title);
    return match || cmesData[0];
  }, [cmesData, selectedSuite]);

  const effectiveSelectedExampleSuite = useMemo(() => {
    if (!exampleSuites?.length || !selectedExampleSuite) return selectedExampleSuite;
    const match = exampleSuites.find((s) => s?.Title === selectedExampleSuite?.Title);
    return match || exampleSuites[0];
  }, [exampleSuites, selectedExampleSuite]);

  const ExampleButtons = [
    {
      title: activeMegaFactory?.toggleButtonText || activeMegaFactory?.Title,
      name: "Composable",
      click: (e) => handleTab(e),
    },
    {
      title: exampleData?.Title,
      name: "Example",
      click: (e) => handleTab(e),
    },
  ];
  const shouldShowButton =
    activeMegaFactory?.noLibrary === false ||
    activeMegaFactory?.showLibrary === true ||
    activeMegaFactory?.showLibrary === "true" ||
    (activeMegaFactory?.library &&
      (activeMegaFactory.library.buttonType === "library" ||
        activeMegaFactory.library.buttonType === "custom"));

  let buttonText = "Add More From Library";
  let buttonURL = "https://tulip.co/library/";

  if (activeMegaFactory?.library?.buttonType === "custom") {
    buttonText = activeMegaFactory.library.customButtonText;
    buttonURL = activeMegaFactory.library.customButtonUrl;
  }

  // Resolve effective UNS config (with ProveIt backward compat shim)
  const effectiveUnsConfig = useMemo(() => {
    if (tab === "Example" && exampleData?.unsConfig?.enabled) {
      return exampleData.unsConfig;
    }
    if (activeMegaFactory?.unsConfig?.enabled) return activeMegaFactory.unsConfig;
    // ProveIt shim: if no unsConfig but has mqttConfig and matches ProveIt ID
    if (
      activeMegaFactory?.id === PROVEIT_MEGAFACTORY_ID &&
      activeMegaFactory?.mqttConfig?.broker &&
      !activeMegaFactory?.unsConfig
    ) {
      return {
        enabled: true,
        mqttConfig: activeMegaFactory.mqttConfig,
        siteFilters: [],
        topicBranches: ['#'],
        defaultFilter: "overall",
        defaultExpandDepth: 4,
      };
    }
    return null;
  }, [activeMegaFactory, tab, exampleData]);

  const showUnsSidebar = !!effectiveUnsConfig?.enabled;
  const effectiveHasSidebar = hasSidebar || showUnsSidebar;

  const innerContent = (
    <div
      ref={ref}
      tabIndex={-1}
      className={effectiveHasSidebar ? "" : "standardGradient"}
      style={{
        display: "flex",
        flexDirection: "column",
        paddingLeft: "20px",
        paddingRight: "20px",
        paddingTop: "40px",
        outline: "none",
        height: effectiveHasSidebar ? "100%" : undefined,
      }}
    >
      {isModal === true && (
        <CloseIcon
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            color: theme.palette.customColors.white,
            cursor: "pointer",
          }}
          onClick={() => {
            handleClose();
          }}
        />
      )}

      {effectiveSelectedSuite && tab !== "Example" && (
        <CmesTabs
          handleSuiteChange={handleSuiteChange}
          selectedSuite={effectiveSelectedSuite}
          suites={cmesData}
          tab={tab}
          onTabClick={handleTabClick}
        />
      )}
      {isExampleMegafactory && effectiveSelectedExampleSuite && (
        <CmesTabs
          handleSuiteChange={handleExampleSuiteChange}
          selectedSuite={effectiveSelectedExampleSuite}
          suites={exampleSuites}
          tab={"Composable"}
          onTabClick={handleTabClick}
        />
      )}
      {tab === "Example" && !isExampleMegafactory &&
        exampleData?.Children?.some((child) => child.flow) && (
          <Process
            beaconData={exampleData.Children}
            selectedAsset={selectedAsset}
            setSelectedAsset={setSelectedAsset}
          />
        )}
      {cmesData ? (
        <FactoryAndPanel
          imgHeight={tab === "Example" ? 200 : 200}
          selectedAsset={selectedAsset}
          setSelectedAsset={setSelectedAsset}
          selectedSuite={
            tab === "Composable"
              ? effectiveSelectedSuite
              : isExampleMegafactory
                ? effectiveSelectedExampleSuite
                : exampleData
          }
          nodes={tab === "Example" && !isExampleMegafactory ? exampleData : null}
          hasSidebar={effectiveHasSidebar}
          preconnectedMqttValues={preconnectedMqttValues}
          preconnectedMqttRecentlyChanged={preconnectedMqttRecentlyChanged}
          simulatedValues={simulatedValues}
          simulatedRecentlyChanged={simulatedRecentlyChanged}
          simulatedAgentStates={simulatedAgentStates}
          simulatedAgents={simulatedAgents}
          megafactoryMqttConfig={activeMegaFactory?.mqttConfig}
          hideDiscover={hideDiscover}
        />
      ) : <img
            alt="Factory floor"
            src={"https://tulip.widen.net/content/u79nz7brvd/webp/transparent-factory-floor-compressed-.webp"
            }
            style={{
              maxWidth: "100%",
              maxHeight: `calc(100vh - 20px)`,
              opacity: 1,
              backgroundColor: "transparent",
            }}
          />}

      <div className="bottom-buttons w-full absolute flex align-items-center items-center text-center md:justify-between pb-3 flex-col md:flex-row bottom-[10px] left-[0] px-[20px]">
        {exampleData && !showUnsSidebar ? (
          <GroupButtons tab={tab} buttonsArray={ExampleButtons} />
        ) : (
          <div></div>
        )}
        <div className="flex flex-row gap-2">
          {shouldShowButton && (
            <CustomButton
              color={"info"}
              endIcon={<OpenInNewIcon />}
              onClick={() => handleMockPc(buttonURL)}
              style={{
                borderRadius: "4px",
                fontSize: "14px",
                padding: "4px 12px",
                fontFamily: "Messina Sans light",
                textTransform: "none",
              }}
            >
              {buttonText}
            </CustomButton>
          )}
          {activeMegaFactory.tulipLibrary && (
            <Gobutton
              text={"Open in Tulip Library"}
              startIcon={
                <SvgIcon
                  className="tulipLibrary"
                  inheritViewBox
                  sx={{
                    height: "18px",
                    width: "auto",
                    color: "customColors.white",
                  }}
                  component={TulipLibrary}
                />
              }
              url={{ type: "beacon", url: activeMegaFactory.tulipLibrary }}
            />
          )}
        </div>
      </div>
      <MockPc />
      <Ipad />
      {isFactoryDirect && (
        <PoweredByLogo
          style={{
            position: "absolute",
            bottom: "10px",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      )}
    </div>
  );

  if (showUnsSidebar) {
    return (
      <div
        className="standardGradient"
        style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: "380px",
            minWidth: "380px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: 1, overflow: "hidden" }}>
            <UnsSidebar
              mqttConfigOverride={effectiveUnsConfig.mqttConfig}
              siteFilters={effectiveUnsConfig.siteFilters}
              topicBranches={effectiveUnsConfig.topicBranches}
              defaultFilter={effectiveUnsConfig.defaultFilter}
              defaultExpandDepth={effectiveUnsConfig.defaultExpandDepth}
            />
          </div>
          {exampleData && (
            <div style={{ padding: "0 20px 22px 20px", flexShrink: 0 }}>
              <GroupButtons tab={tab} buttonsArray={ExampleButtons} />
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
          {innerContent}
        </div>
      </div>
    );
  }

  return innerContent;
});

export default MegaFactoryContent;
